import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { PositionPayGapData } from "../../PaygapPayGradesOverview";
import {
  CHART_HEIGHT,
  SIDE_PADDING,
  useChartDimensions,
  useMaxValue,
} from "../../hooks";

const BOTTOM_PADDING = 120; // Space for rotated labels
const TOP_PADDING = 30;

interface BarChartProps {
  data: PositionPayGapData[];
  yAxisLabel: string;
  xAxisLabel: string;
}

export const BarChart: FC<BarChartProps> = ({ data, yAxisLabel, xAxisLabel }) => {
  const { t } = useTranslation();
  const { barWidth, chartWidth, leftPadding, rightPadding, barSpacing } =
    useChartDimensions(data);
  const maxValue = useMaxValue(data);

  const formatValue = (value: number): string => {
    const isPercentage = yAxisLabel?.includes("%") || false;

    if (isPercentage) {
      return `${value.toFixed(1)}%`;
    }

return value.toFixed(1);
  };

  // Generate grid lines
  const gridLines: number[] = [];
  const step = maxValue / 5;

  for (let i = -maxValue; i <= maxValue; i += step) {
    gridLines.push(Math.round(i * 10) / 10);
  }

  return (
    <Box display="flex" justifyContent="center">
      {/* Y-axis label - outside scrollable area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            transform: "rotate(-90deg)",
            whiteSpace: "nowrap",
            width: "200px",
            textAlign: "center",
          }}
        >
          {yAxisLabel}
        </Typography>
      </Box>

      {/* Chart area */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Box
          position="relative"
          width={chartWidth}
          height={CHART_HEIGHT}
          pb={4}
        >

        {/* Chart area */}
        <svg width={chartWidth} height={CHART_HEIGHT}>
          {/* Grid lines */}
          {gridLines.map((value) => {
            const chartAreaHeight = CHART_HEIGHT - BOTTOM_PADDING - TOP_PADDING;
            const y =
              TOP_PADDING +
              chartAreaHeight -
              ((value + maxValue) / (2 * maxValue)) * chartAreaHeight;
            const gridLineStartX = leftPadding;
            const gridLineEndX = chartWidth - rightPadding;

            return (
              <g key={value}>
                <line
                  x1={gridLineStartX}
                  y1={y}
                  x2={gridLineEndX}
                  y2={y}
                  stroke="#e0e0e0"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
                <text
                  x={leftPadding - 10}
                  y={y + 5}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {formatValue(value)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {data.length > 0 ? (
            (() => {
              // Calculate starting X position to center bars in the chart
              const totalBarsWidth = data.length * barWidth;
              const totalSpacingWidth = (data.length - 1) * barSpacing;
              const totalContentWidth = totalBarsWidth + totalSpacingWidth;
              const chartContentWidth = chartWidth - SIDE_PADDING;
              const startX =
                leftPadding + (chartContentWidth - totalContentWidth) / 2;

              const chartAreaHeight = CHART_HEIGHT - BOTTOM_PADDING - TOP_PADDING;

              return data.map((item, index) => {
                const x = startX + index * (barWidth + barSpacing);
                const centerY = TOP_PADDING + chartAreaHeight / 2;
                const barHeight =
                  (Math.abs(item.payGap) * chartAreaHeight) / (2 * maxValue);
                const barY = item.payGap < 0 ? centerY : centerY - barHeight;
                const barColor = item.payGap < 0 ? "#f44336" : "#4caf50";

                return (
                  <g key={item.position}>
                    {/* Bar */}
                    <rect
                      x={x}
                      y={barY}
                      width={barWidth}
                      height={barHeight}
                      fill={barColor}
                      opacity={0.7}
                    />
                    {/* Description text (rotated for long labels) */}
                    <text
                      x={x + barWidth / 2}
                      y={CHART_HEIGHT - BOTTOM_PADDING + 15}
                      textAnchor="end"
                      fontSize="10"
                      fill="#666"
                      transform={`rotate(-45 ${x + barWidth / 2} ${CHART_HEIGHT - BOTTOM_PADDING + 15})`}
                    >
                      {item.description}
                    </text>
                    {/* Value label */}
                    <text
                      x={x + barWidth / 2}
                      y={item.payGap < 0 ? barY - 5 : barY + barHeight + 15}
                      textAnchor="middle"
                      fontSize="10"
                      fill={barColor}
                      fontWeight="bold"
                    >
                      {formatValue(item.payGap)}
                    </text>
                  </g>
                );
              });
            })()
          ) : (
            <text
              x={chartWidth / 2}
              y={CHART_HEIGHT / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#999"
            >
              {t("paygap.payGapPerPosition.noData")}
            </text>
          )}
        </svg>

          {/* X-axis label */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: -35,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "0.875rem",
              width: "200px",
              textAlign: "center",
            }}
          >
            {xAxisLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
